/**
 * RFC 9457 problem details, as returned by the Dashboard API.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  /** Backend's machine-readable discriminator. See ACCESS_NOT_PROVISIONED_CODES. */
  code?: string;
  [key: string]: unknown;
}

/**
 * ---------------------------------------------------------------------------
 * OPEN QUESTION #5 — must be agreed with the backend before either side ships.
 * ---------------------------------------------------------------------------
 * The 401 that means "your sign-in is fine, but you are not provisioned
 * for this dashboard" has to be distinguishable from every other 401. Both
 * plans describe it by behaviour and neither pins the value, and a mismatch
 * here does not surface as an obvious error — it surfaces as an infinite
 * redirect loop for exactly the users invite-only onboarding creates.
 *
 * The values below are placeholders. Replace them with the agreed contract and
 * delete this notice.
 */
export const ACCESS_NOT_PROVISIONED_CODES = ['access_not_provisioned', 'user_deactivated'] as const;

/** The header the backend sets on an expired-token 401, and only then. */
export const TOKEN_EXPIRED_HEADER = 'Token-Expired';

export function isAccessNotProvisioned(problem: ProblemDetails | null): boolean {
  if (!problem) return false;
  const code = problem.code ?? problem.type ?? '';
  return ACCESS_NOT_PROVISIONED_CODES.some((c) => code.includes(c));
}

/**
 * The two MFA 401s, agreed with the backend
 * (tollit-fulfillment-dotnet-functions:
 * docs/dashboard/2026-09-24-dashboard-mfa-totp-sms-recovery-plan.md, "Error-code
 * contract"). The backend writes them to ProblemDetails.type.
 *
 * Matched exactly, never by substring: a substring match is how
 * isAccessNotProvisioned above works, and the two families must not be able
 * to shadow each other.
 */
export const MFA_ENROLLMENT_REQUIRED_CODE = 'mfa_enrollment_required';
/** Signed in with the SMS recovery factor: the API grants nothing until TOTP is re-enrolled. */
export const MFA_TOTP_REENROLLMENT_REQUIRED_CODE = 'mfa_totp_reenrollment_required';

function hasExactCode(problem: ProblemDetails | null, code: string): boolean {
  if (!problem) return false;
  return problem.type === code || problem.code === code;
}

export function isMfaEnrollmentRequired(problem: ProblemDetails | null): boolean {
  return hasExactCode(problem, MFA_ENROLLMENT_REQUIRED_CODE);
}

export function isMfaTotpReenrollmentRequired(problem: ProblemDetails | null): boolean {
  return hasExactCode(problem, MFA_TOTP_REENROLLMENT_REQUIRED_CODE);
}
